/**
 * D2 — *"the date and the sentence under it have to agree"*.
 *
 * 🔴 THE ROSTER PRINTS A LAST-PLAYED DATE AND A RELATIVE LINE UNDER IT, and the
 * two used to be computed by different arithmetic: the date came off the local
 * calendar, the "N days ago" came off `Math.floor(elapsed / 86_400_000)`. Those
 * disagree whenever the child played later in the day than the moment the
 * teacher is looking — a session at 22:00 on the 2nd, read at 09:00 on the 22nd,
 * is 19.4 elapsed days and 20 calendar days, so the table said "2 Aug" over
 * "19 days ago" and asked the teacher to decide which half to believe.
 *
 * Both halves now come from the same calendar difference, so the sentence is
 * always a reading of the date printed above it and never a second opinion.
 *
 * Pure and clock-injected, because "how long ago" is the one thing on this
 * screen that changes without anybody touching the data.
 */

/** Midnight local — the boundary a teacher means by "day", not a 24-hour span. */
function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Whole calendar days from `from` to `to`, counted in the reader's own zone.
 *
 * ⚠️ `Math.round`, NOT `Math.floor`. A DST day is 23 or 25 hours long, and
 * flooring a 23-hour gap between two midnights returns 0 days for what every
 * calendar in the country calls one.
 */
export function calendarDaysBetween(from: Date, to: Date): number {
  const ms = startOfLocalDay(to).getTime() - startOfLocalDay(from).getTime();
  return Math.round(ms / 86_400_000);
}

export interface LastPlayedText {
  /** The calendar date, unambiguous in any locale — "2 Aug 2026", never "8/2/2026". */
  date: string;
  /** The line under it. Empty when the child has never played. */
  relative: string;
}

/**
 * Both lines of the Last played cell.
 *
 * `fallback` is what a child who has never played gets — the copy table's
 * "Not started" — and it is also what an unparseable timestamp gets, because a
 * date the browser could not read is not a date worth guessing at.
 */
export function describeLastPlayed(
  lastPlayed: string | null,
  fallback: string,
  now: Date = new Date(),
): LastPlayedText {
  if (!lastPlayed) return { date: fallback, relative: "" };
  const played = new Date(lastPlayed);
  if (Number.isNaN(played.getTime())) return { date: fallback, relative: "" };

  const date = played.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  /*
   * ⚠️ A FUTURE TIMESTAMP READS AS "Today" RATHER THAN "-2 days ago". Tablets
   * run with wrong clocks and the backend keeps a client stamp that is within
   * its skew window, so this cell can legitimately be handed tomorrow. Saying
   * "Today" is the smallest untruth available; negative arithmetic on screen is
   * the largest.
   */
  const days = calendarDaysBetween(played, now);
  if (days <= 0) return { date, relative: "Today" };
  if (days === 1) return { date, relative: "Yesterday" };
  return { date, relative: `${String(days)} days ago` };
}
